/**
 * RiskProfilePanel — Phase 2D PR-3 (Feb 2026)
 * ---------------------------------------------------------------------------
 * Reusable risk & safety surface. Three mount modes:
 *
 *   mode="full"           — Participant Detail → Risk Profile sub-tab.
 *                           Calls GET /api/participants/{pid}/risk-profile.
 *                           Shows full clinical detail (admin/coordinator).
 *
 *   mode="shift_context"  — ShiftDetail safety widget.
 *                           Calls GET /api/participants/{pid}/risk-profile?view=shift_context.
 *                           Shows redacted flag chips + counts only.
 *                           Compact, severity-coloured. No clinical free-text.
 *
 *   mode="intake_preview" — Intake Clinical section.
 *                           PURELY CLIENT-SIDE — no API call (no participant yet).
 *                           Computes high-confidence flags from the in-progress
 *                           intake form state (allergies/conditions/contacts).
 *                           Matches the backend EXTRA-4 hook rules exactly so
 *                           the coordinator sees what WILL be auto-flagged on
 *                           activation.
 *
 * Feature flag: when `participant_risk_profile_enabled` is OFF the component
 * renders nothing for full/shift_context modes (backend returns 404). The
 * intake_preview mode is purely client-side so it works regardless.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ShieldAlert, RefreshCw, Activity, HeartPulse, Brain, Wrench, MapPin, FileWarning, Phone, AlertCircle } from 'lucide-react';

const SEVERITY_TONE = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-300',
  LOW: 'bg-slate-100 text-slate-700 border-slate-300',
  NONE: 'bg-muted text-muted-foreground border-muted',
};

const FLAG_ICON = {
  ANAPHYLAXIS_PRESENT: AlertTriangle,
  SEVERE_ALLERGY: AlertTriangle,
  SEIZURE_RISK: Brain,
  MANUAL_HANDLING_RISK: Wrench,
  MANUAL_HANDLING_ALERT: Wrench,
  FALLS_RISK: Activity,
  BEHAVIOUR_RISK: HeartPulse,
  WANDERING_RISK: MapPin,
  RESTRICTIVE_PRACTICE_ACTIVE: FileWarning,
  RESTRICTIVE_PRACTICE_REVIEW_OVERDUE: FileWarning,
  BEHAVIOUR_SUPPORT_PLAN_ACTIVE: HeartPulse,
  BEHAVIOUR_SUPPORT_ALERT: HeartPulse,
  INCIDENT_WATCH_90D: AlertCircle,
  INCIDENT_WATCH: AlertCircle,
  NO_EMERGENCY_CONTACT: Phone,
  EMERGENCY_CONSIDERATION: ShieldAlert,
  ALLERGY: AlertTriangle,
  MEDICATION_ALERT: AlertTriangle,
  RESTRICTIVE_PRACTICE_ALERT: FileWarning,
};

function FlagChip({ flag }) {
  const Icon = FLAG_ICON[flag.flag_key] || ShieldAlert;
  const tone = SEVERITY_TONE[flag.severity] || SEVERITY_TONE.LOW;
  return (
    <Badge variant="outline" className={`${tone} text-xs gap-1 px-2 py-0.5`}
           data-testid={`risk-flag-${flag.flag_key}`}>
      <Icon className="h-3 w-3" />
      <span className="font-medium">{flag.label || flag.flag_key}</span>
    </Badge>
  );
}

function OverallBanner({ severity, count }) {
  if (severity === 'NONE' || !severity) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
           data-testid="risk-overall-banner">
        No safety risks detected.
      </div>
    );
  }
  const tone = SEVERITY_TONE[severity] || SEVERITY_TONE.MEDIUM;
  return (
    <div className={`rounded-md border px-3 py-2 text-sm flex items-center gap-2 ${tone}`}
         data-testid="risk-overall-banner">
      <ShieldAlert className="h-4 w-4" />
      <span className="font-semibold">Overall severity: {severity}</span>
      <span className="opacity-70">· {count} flag(s)</span>
    </div>
  );
}


// ============================================================================
// Client-side computation for intake_preview mode
// Mirrors backend EXTRA-4 rules in services/intake/promotion.py
// ============================================================================
function computeIntakePreviewFlags(intakeData) {
  const flags = [];
  const allergies = intakeData?.allergies || [];
  const conditions = intakeData?.conditions || [];
  const contacts = intakeData?.contacts || [];

  const anaphylaxis = allergies.filter(a => (a?.severity || '').toUpperCase() === 'ANAPHYLAXIS').length;
  const severe = allergies.filter(a => (a?.severity || '').toUpperCase() === 'SEVERE').length;
  if (anaphylaxis > 0) {
    flags.push({ flag_key: 'ANAPHYLAXIS_PRESENT', severity: 'CRITICAL',
                 label: `Anaphylaxis on file (${anaphylaxis})` });
  } else if (severe > 0) {
    flags.push({ flag_key: 'SEVERE_ALLERGY', severity: 'HIGH',
                 label: `Severe allergy (${severe})` });
  }

  const mh = conditions.filter(c => (c?.category || '').toUpperCase() === 'MANUAL_HANDLING').length;
  if (mh > 0) {
    flags.push({ flag_key: 'MANUAL_HANDLING_RISK', severity: 'HIGH',
                 label: `Manual handling considerations (${mh})` });
  }

  const seizure = conditions.some(c =>
    c?.is_seizure_disorder === true ||
    /seizure|epilepsy/i.test(c?.condition_name || ''));
  if (seizure) {
    flags.push({ flag_key: 'SEIZURE_RISK', severity: 'HIGH',
                 label: 'Seizure disorder noted' });
  }

  const hasEmergency = contacts.some(c => !!(c?.is_emergency || c?.isEmergency));
  if (!hasEmergency) {
    flags.push({ flag_key: 'NO_EMERGENCY_CONTACT', severity: 'HIGH',
                 label: 'No emergency contact added yet' });
  }

  const order = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1, NONE: 0 };
  flags.sort((a, b) => (order[b.severity] || 0) - (order[a.severity] || 0));

  const overall = flags.length ? flags[0].severity : 'NONE';
  return { flags, overall };
}


// ============================================================================
// Main component
// ============================================================================
export function RiskProfilePanel({
  participantId,
  mode = 'full',
  intakeData = null,
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [flagEnabled, setFlagEnabled] = useState(null);

  const loadFlag = useCallback(async () => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      setFlagEnabled(!!flags.participant_risk_profile_enabled);
    } catch { setFlagEnabled(false); }
  }, []);

  const loadProfile = useCallback(async () => {
    if (mode === 'intake_preview') return;       // no API call needed
    if (!participantId) return;
    if (flagEnabled !== true) {                  // ⛔ Hard gate on client flag
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = mode === 'shift_context'
        ? `/participants/${participantId}/risk-profile?view=shift_context`
        : `/participants/${participantId}/risk-profile`;
      const r = await api.get(url);
      setData(r.data?.data || r.data || null);
    } catch (e) {
      const code = e.response?.data?.error?.code || e.response?.data?.detail?.code;
      if (code === 'FEATURE_DISABLED') {
        setData(null);
        setFlagEnabled(false);
      } else {
        setError(e.response?.data?.error?.message || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [participantId, mode, flagEnabled]);

  useEffect(() => { loadFlag(); }, [loadFlag]);
  useEffect(() => { loadProfile(); }, [loadProfile]);

  // intake_preview computes from props synchronously
  const preview = useMemo(() => {
    if (mode !== 'intake_preview') return null;
    return computeIntakePreviewFlags(intakeData);
  }, [mode, intakeData]);

  // ----- INTAKE PREVIEW (client-only, always works) -----
  if (mode === 'intake_preview') {
    if (!preview || preview.flags.length === 0) {
      return (
        <Card className="border-dashed" data-testid="risk-panel-intake-preview">
          <CardContent className="p-3 text-xs text-muted-foreground">
            No high-confidence safety risks detected from intake data yet. Add anaphylaxis-grade allergies, manual-handling conditions, or seizure/epilepsy conditions to surface them here. An emergency contact is also recommended before activation.
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="border-amber-200" data-testid="risk-panel-intake-preview">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-700" />
            <span className="text-xs font-semibold text-amber-800">
              Risk preview — these flags will be auto-created on activation
            </span>
          </div>
          <OverallBanner severity={preview.overall} count={preview.flags.length} />
          <div className="flex flex-wrap gap-1.5 pt-1">
            {preview.flags.map((f) => <FlagChip key={f.flag_key} flag={f} />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ----- FEATURE FLAG OFF (server-driven modes) -----
  // Hard gate: never render anything (no loading skeleton, no banner, no card) when client flag is OFF.
  // Intake preview is purely client-side and unaffected.
  if (flagEnabled !== true) {
    return null;
  }

  if (loading) {
    return (
      <Card data-testid={`risk-panel-${mode}-loading`}>
        <CardContent className="p-3"><Skeleton className="h-6 w-full mb-2" /><Skeleton className="h-4 w-3/4" /></CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200" data-testid={`risk-panel-${mode}-error`}>
        <CardContent className="p-3 text-xs text-red-700">Could not load risk profile: {error}</CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // ----- SHIFT CONTEXT (compact, redacted) -----
  if (mode === 'shift_context') {
    const flags = data.flags || [];
    if (flags.length === 0 && data.overall_severity === 'NONE') {
      return (
        <Card className="border-emerald-200 bg-emerald-50/60" data-testid="risk-panel-shift-context">
          <CardContent className="p-3 text-xs text-emerald-800 flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5" /> No safety alerts on file for this participant.
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="border-orange-300 bg-orange-50/40" data-testid="risk-panel-shift-context">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-orange-700" />
              <span className="text-xs font-semibold text-orange-900">Safety alerts</span>
              <Badge variant="outline" className={`${SEVERITY_TONE[data.overall_severity] || SEVERITY_TONE.MEDIUM} text-[10px] px-1.5`}>
                {data.overall_severity}
              </Badge>
            </div>
            <Button size="sm" variant="ghost" onClick={loadProfile} data-testid="risk-panel-shift-refresh">
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {flags.map((f) => <FlagChip key={f.flag_key} flag={f} />)}
          </div>
          {data.summary && (
            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3 pt-1">
              {data.summary.anaphylaxis_count > 0 && <span>Anaphylaxis: <b>{data.summary.anaphylaxis_count}</b></span>}
              {data.summary.active_restrictive_practices_count > 0 && <span>Active RPs: <b>{data.summary.active_restrictive_practices_count}</b></span>}
              {data.summary.incidents_last_90d > 0 && <span>Incidents 90d: <b>{data.summary.incidents_last_90d}</b></span>}
              {data.summary.has_emergency_contact === false && <span className="text-orange-700">No emergency contact</span>}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ----- FULL MODE (participant Risk Profile sub-tab) -----
  const allergies = data.allergies || [];
  const conditions = data.conditions || [];
  const medications = data.medications || [];
  const rps = data.restrictive_practices || [];
  const risks = data.risks || [];
  const incidents = data.incidents_recent || [];
  const persisted = data.persisted_flags || [];

  return (
    <div className="space-y-3" data-testid="risk-panel-full">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-primary" /> Consolidated Risk Profile
        </h3>
        <Button size="sm" variant="outline" onClick={loadProfile} data-testid="risk-panel-full-refresh">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
      </div>

      <OverallBanner severity={data.overall_severity} count={(data.flags || []).length} />

      {data.flags?.length > 0 && (
        <Card><CardContent className="p-3">
          <div className="text-xs font-semibold mb-2">Computed flags</div>
          <div className="flex flex-wrap gap-1.5">{data.flags.map((f) => <FlagChip key={`${f.flag_key}-${f.source_module}`} flag={f} />)}</div>
        </CardContent></Card>
      )}

      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Allergies ({allergies.length})</div>
            {allergies.length === 0 ? <p className="text-xs text-muted-foreground">None on file.</p> :
              <ul className="text-xs space-y-1">{allergies.slice(0, 10).map((a) => (
                <li key={a.id} className="flex items-center gap-2" data-testid={`risk-panel-allergy-${a.id}`}>
                  <Badge variant="outline" className={`${SEVERITY_TONE[a.severity]} text-[10px] px-1.5`}>{a.severity}</Badge>
                  <span className="font-medium">{a.allergen}</span>
                  {a.reaction && <span className="text-muted-foreground">— {a.reaction}</span>}
                </li>
              ))}</ul>}
          </CardContent></Card>

          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Health conditions ({conditions.length})</div>
            {conditions.length === 0 ? <p className="text-xs text-muted-foreground">None on file.</p> :
              <ul className="text-xs space-y-1">{conditions.slice(0, 10).map((c) => (
                <li key={c.id} className="flex items-center gap-2" data-testid={`risk-panel-condition-${c.id}`}>
                  <Badge variant="outline" className={`${SEVERITY_TONE[c.severity]} text-[10px] px-1.5`}>{c.severity}</Badge>
                  <span className="font-medium">{c.condition_name}</span>
                  {c.is_seizure_disorder && <span className="text-orange-700 text-[10px]">[seizure]</span>}
                </li>
              ))}</ul>}
          </CardContent></Card>

          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Medications ({medications.length})</div>
            {medications.length === 0 ? <p className="text-xs text-muted-foreground">None on file.</p> :
              <ul className="text-xs space-y-1">{medications.slice(0, 10).map((m) => (
                <li key={m.id}>{m.medication_name}{m.dose ? ` · ${m.dose}` : ''}{m.is_prn ? ' (PRN)' : ''}</li>
              ))}</ul>}
          </CardContent></Card>

          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Restrictive practices ({rps.length})</div>
            {rps.length === 0 ? <p className="text-xs text-muted-foreground">None on file.</p> :
              <ul className="text-xs space-y-1">{rps.slice(0, 10).map((rp) => (
                <li key={rp.id} className="flex items-center gap-2">
                  <Badge variant="outline" className={`${SEVERITY_TONE[rp.severity]} text-[10px] px-1.5`}>{rp.status}</Badge>
                  <span>{rp.type}</span>
                  {rp.review_overdue && <span className="text-red-700 text-[10px]">[review overdue]</span>}
                </li>
              ))}</ul>}
          </CardContent></Card>

          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Active risk assessments ({risks.length})</div>
            {risks.length === 0 ? <p className="text-xs text-muted-foreground">None on file.</p> :
              <ul className="text-xs space-y-1">{risks.slice(0, 10).map((r) => (
                <li key={r.id} className="flex items-center gap-2">
                  <Badge variant="outline" className={`${SEVERITY_TONE[r.severity]} text-[10px] px-1.5`}>{r.severity}</Badge>
                  <span className="font-medium">{r.risk_type}</span> · {r.title || r.risk_no}
                </li>
              ))}</ul>}
          </CardContent></Card>

          <Card><CardContent className="p-3">
            <div className="text-xs font-semibold mb-2">Incidents — last 90 days ({incidents.length})</div>
            {incidents.length === 0 ? <p className="text-xs text-muted-foreground">No recent incidents.</p> :
              <ul className="text-xs space-y-1">{incidents.slice(0, 10).map((i) => (
                <li key={i.id} className="flex items-center gap-2">
                  <Badge variant="outline" className={`${SEVERITY_TONE[i.severity] || SEVERITY_TONE.LOW} text-[10px] px-1.5`}>{i.severity}</Badge>
                  <span className="font-medium">{i.incident_no}</span>
                  {i.type && <span className="text-muted-foreground">· {i.type}</span>}
                </li>
              ))}</ul>}
          </CardContent></Card>

          {persisted.length > 0 && (
            <Card className="md:col-span-2"><CardContent className="p-3">
              <div className="text-xs font-semibold mb-2">Persisted flags ({persisted.length}) — canonical preferred</div>
              <ul className="text-xs space-y-1">{persisted.map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Badge variant="outline" className={`${SEVERITY_TONE[f.severity]} text-[10px] px-1.5`}>{f.severity}</Badge>
                  <span className="font-medium">{f.flag_type}</span>
                  <span className="text-[10px] text-muted-foreground">({f.source})</span>
                  {f.notes && <span className="text-muted-foreground">— {f.notes}</span>}
                </li>
              ))}</ul>
            </CardContent></Card>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground text-center">
        Severity ladder: {(data.severity_ladder || []).join(' < ')} · Generated {data.generated_at}
      </p>
    </div>
  );
}

export default RiskProfilePanel;
